import type { ExtrudeGeometry } from 'three';

/**
 * Split the caps group of an ExtrudeGeometry into separate inner and outer cap groups.
 *
 * ExtrudeGeometry (bevelEnabled=false) produces 2 groups:
 *   - Group 0: cap faces (front + back), materialIndex 0
 *   - Group 1: side faces (extruded walls), materialIndex 1
 *
 * After this function, the geometry has 3 groups:
 *   - Group 0: inner cap faces (z < threshold)  -> materialIndex 0  (plain)
 *   - Group 1: side faces                        -> materialIndex 1  (plain)
 *   - Group 2: outer cap faces (z >= threshold)  -> materialIndex 2  (TEXTURED)
 *
 * The outward cap is LAST deliberately: `cloneMaterial` carries the surround texture across CSG
 * regeneration by taking `mat[mat.length - 1]`, so the textured material has to be the final entry
 * or every regeneration would adopt a flat material as the finish.
 *
 * Local +Z is the outward face for every panel: panels are rotated by `originAngle` about Y only,
 * so a front panel's +Z points away from the island centre and a back panel's (180 degrees) does too.
 * That is the face shown in the 2D panel view -- the only one that carries the finish in manufacturing.
 *
 * Supports both indexed and non-indexed geometries.
 *
 * @param geometry   The ExtrudeGeometry (mutated in place)
 * @param isCentered true if geometry.center() was applied (shifts caps symmetrically around z=0)
 * @param depth      The extrusion depth used when creating the geometry
 * @returns true if the split succeeded; false if the geometry format was unexpected
 */
export const splitExtrudeCaps = (
	geometry: ExtrudeGeometry,
	isCentered: boolean,
	depth: number
): boolean => {
	if (geometry.groups.length !== 2) {
		return false;
	}

	const capsGroup = geometry.groups[0];   // caps come first in ExtrudeGeometry
	const sidesGroup = geometry.groups[1];  // sides come second
	const positions = geometry.attributes.position;

	// Z-threshold: midpoint between inner and outer caps
	// Centered (Surround): inner at -depth/2, outer at +depth/2 -> threshold = 0
	// Non-centered: inner at 0, outer at depth -> threshold = depth/2
	const zThreshold = isCentered ? 0 : depth / 2;

	if (geometry.index !== null) {
		// === Indexed geometry path ===
		const indexArray = geometry.index.array;
		const innerIndices: number[] = [];
		const outerIndices: number[] = [];

		for (let i = capsGroup.start; i < capsGroup.start + capsGroup.count; i += 3) {
			const idx0 = indexArray[i];
			const idx1 = indexArray[i + 1];
			const idx2 = indexArray[i + 2];

			const avgZ = (positions.getZ(idx0) + positions.getZ(idx1) + positions.getZ(idx2)) / 3;

			if (avgZ < zThreshold) {
				innerIndices.push(idx0, idx1, idx2);
			} else {
				outerIndices.push(idx0, idx1, idx2);
			}
		}

		let writePos = capsGroup.start;
		for (let i = 0; i < innerIndices.length; i++) {
			indexArray[writePos++] = innerIndices[i];
		}
		for (let i = 0; i < outerIndices.length; i++) {
			indexArray[writePos++] = outerIndices[i];
		}

		geometry.clearGroups();
		geometry.addGroup(capsGroup.start, innerIndices.length, 0);
		geometry.addGroup(sidesGroup.start, sidesGroup.count, 1);
		geometry.addGroup(capsGroup.start + innerIndices.length, outerIndices.length, 2);

		geometry.index.needsUpdate = true;
	} else {
		// === Non-indexed geometry path ===
		const capsStart = capsGroup.start;
		const capsCount = capsGroup.count;

		const innerTriStarts: number[] = [];
		const outerTriStarts: number[] = [];

		for (let v = capsStart; v < capsStart + capsCount; v += 3) {
			const avgZ = (positions.getZ(v) + positions.getZ(v + 1) + positions.getZ(v + 2)) / 3;
			if (avgZ < zThreshold) {
				innerTriStarts.push(v);
			} else {
				outerTriStarts.push(v);
			}
		}

		const newTriOrder = [...innerTriStarts, ...outerTriStarts];
		const innerVertexCount = innerTriStarts.length * 3;
		const outerVertexCount = outerTriStarts.length * 3;

		// Rearrange all vertex attributes within the caps range
		for (const attrName of Object.keys(geometry.attributes)) {
			const attr = geometry.attributes[attrName];
			const itemSize = attr.itemSize;
			const srcArray = attr.array;

			const tempData = srcArray.slice(capsStart * itemSize, (capsStart + capsCount) * itemSize);

			let destVertex = capsStart;
			for (const triStart of newTriOrder) {
				const srcOffsetInCaps = triStart - capsStart;
				for (let v = 0; v < 3; v++) {
					const srcIdx = (srcOffsetInCaps + v) * itemSize;
					const destIdx = destVertex * itemSize;
					for (let c = 0; c < itemSize; c++) {
						srcArray[destIdx + c] = tempData[srcIdx + c];
					}
					destVertex++;
				}
			}

			attr.needsUpdate = true;
		}

		geometry.clearGroups();
		geometry.addGroup(capsStart, innerVertexCount, 0);
		geometry.addGroup(sidesGroup.start, sidesGroup.count, 1);
		geometry.addGroup(capsStart + innerVertexCount, outerVertexCount, 2);
	}

	return true;
};
